import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteSuperAdmin,
  disableSuperAdmin,
  enableSuperAdmin,
  inviteSuperAdmin,
  listSuperAdmins,
  resendSuperAdminInvite,
  type SuperAdminRow,
} from "@/lib/api";
import { useAuth } from "@/contexts/auth";
import {
  Badge,
  Button,
  Card,
  ErrorBanner,
  Field,
  Modal,
  PageContainer,
  TextInput,
  toErrorMessage,
  useConfirm,
} from "@/components/Forms";

/**
 * Phase 35 Slice D — platform super-admin management. List peers + invite a
 * new one. The invitee is created with no password and gets a
 * `/admin/password-reset/<token>` email; the set-password link is also shown
 * here so onboarding works even when platform email isn't configured.
 */
type ResendResult = { email: string; inviteSent: boolean; setPasswordUrl: string };

export default function SuperAdminsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [rows, setRows] = useState<SuperAdminRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [reloadAt, setReloadAt] = useState(0);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [resendResult, setResendResult] = useState<ResendResult | null>(null);

  const rowBusy = (id: number) => resendingId === id || actingId === id;

  useEffect(() => {
    let cancelled = false;
    listSuperAdmins()
      .then((r) => !cancelled && setRows(r.superAdmins))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("superAdmins.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [reloadAt, t]);

  async function resend(a: SuperAdminRow) {
    setError(null);
    setResendingId(a.id);
    try {
      const r = await resendSuperAdminInvite(a.id);
      setResendResult({ email: a.email, inviteSent: r.inviteSent, setPasswordUrl: r.setPasswordUrl });
    } catch (e) {
      setError(toErrorMessage(e));
    } finally {
      setResendingId(null);
    }
  }

  // Translate the opaque {error} codes the lifecycle endpoints can return;
  // fall back to whatever toErrorMessage surfaces for anything else.
  function actionError(e: unknown): string {
    const code = toErrorMessage(e);
    const known: Record<string, string> = {
      cannot_modify_self: t("superAdmins.errSelf"),
      last_admin: t("superAdmins.errLastAdmin"),
      not_found: t("superAdmins.errNotFound"),
    };
    return known[code] ?? code;
  }

  async function act(a: SuperAdminRow, fn: () => Promise<unknown>) {
    setError(null);
    setActingId(a.id);
    try {
      await fn();
      setReloadAt((n) => n + 1);
    } catch (e) {
      setError(actionError(e));
    } finally {
      setActingId(null);
    }
  }

  async function block(a: SuperAdminRow) {
    const ok = await confirm({
      title: t("superAdmins.confirmBlockTitle", { name: a.fullName ?? a.email }),
      body: t("superAdmins.confirmBlockBody"),
      confirmLabel: t("superAdmins.block"),
      destructive: true,
    });
    if (ok) await act(a, () => disableSuperAdmin(a.id));
  }

  function unblock(a: SuperAdminRow) {
    return act(a, () => enableSuperAdmin(a.id));
  }

  async function remove(a: SuperAdminRow) {
    const ok = await confirm({
      title: t("superAdmins.confirmRemoveTitle", { name: a.fullName ?? a.email }),
      body: t("superAdmins.confirmRemoveBody"),
      confirmLabel: t("superAdmins.remove"),
      destructive: true,
    });
    if (ok) await act(a, () => deleteSuperAdmin(a.id));
  }

  return (
    <PageContainer
      title={t("superAdmins.title")}
      subtitle={t("superAdmins.subtitle")}
      actions={
        <Button variant="primary" onClick={() => setInviting(true)}>
          {t("superAdmins.invite")}
        </Button>
      }
    >
      <ErrorBanner message={error} />
      <Card>
        {rows === null ? (
          <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("superAdmins.empty")}</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left py-1">{t("superAdmins.colAdmin")}</th>
                <th className="text-left py-1">{t("superAdmins.colStatus")}</th>
                <th className="text-left py-1 hidden md:table-cell">{t("superAdmins.colLastLogin")}</th>
                <th className="text-right py-1">{t("superAdmins.colActions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((a) => (
                <tr key={a.id}>
                  <td className="py-1.5">
                    <div className="font-medium">{a.fullName ?? a.email}</div>
                    <div className="text-xs text-muted-foreground">{a.email}</div>
                  </td>
                  <td className="py-1.5">
                    {!a.enabled ? (
                      <Badge tone="warning">{t("superAdmins.statusDisabled")}</Badge>
                    ) : !a.hasPassword ? (
                      <Badge tone="info">{t("superAdmins.statusInvited")}</Badge>
                    ) : (
                      <span className="text-xs text-emerald-400">{t("superAdmins.statusActive")}</span>
                    )}
                  </td>
                  <td className="py-1.5 text-xs text-muted-foreground hidden md:table-cell">
                    {a.lastLoginAt ?? "—"}
                  </td>
                  <td className="py-1.5 text-right">
                    {a.id === user?.id ? (
                      <span className="text-xs text-muted-foreground">{t("superAdmins.you")}</span>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        {a.enabled && !a.hasPassword && (
                          <Button onClick={() => resend(a)} disabled={rowBusy(a.id)}>
                            {resendingId === a.id
                              ? t("superAdmins.resending")
                              : t("superAdmins.resend")}
                          </Button>
                        )}
                        {a.enabled ? (
                          <Button variant="ghost" onClick={() => block(a)} disabled={rowBusy(a.id)}>
                            {t("superAdmins.block")}
                          </Button>
                        ) : (
                          <Button onClick={() => unblock(a)} disabled={rowBusy(a.id)}>
                            {t("superAdmins.unblock")}
                          </Button>
                        )}
                        <Button
                          variant="destructive"
                          onClick={() => remove(a)}
                          disabled={rowBusy(a.id)}
                        >
                          {t("superAdmins.remove")}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <InviteSuperAdminModal
        open={inviting}
        onClose={() => setInviting(false)}
        onInvited={() => setReloadAt((n) => n + 1)}
      />
      <ResendResultModal result={resendResult} onClose={() => setResendResult(null)} />
    </PageContainer>
  );
}

/**
 * Phase 36 — result panel shown after a resend: confirms the email went out, or
 * (when the platform sender is down) surfaces the fresh set-password link to copy.
 */
function ResendResultModal({
  result,
  onClose,
}: {
  result: ResendResult | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={result !== null}
      title={t("superAdmins.resendResultTitle")}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={onClose}>
          {t("forms.close")}
        </Button>
      }
    >
      {result && (
        <div className="space-y-3">
          <p className="text-sm">
            {result.inviteSent
              ? t("superAdmins.resendSentTo", { email: result.email })
              : t("superAdmins.inviteNotEmailed")}
          </p>
          <Field label={t("superAdmins.setPasswordLink")}>
            <TextInput readOnly value={result.setPasswordUrl} onFocus={(e) => e.target.select()} />
          </Field>
        </div>
      )}
    </Modal>
  );
}

function InviteSuperAdminModal({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ inviteSent: boolean; setPasswordUrl: string } | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setFullName("");
      setErr(null);
      setResult(null);
    }
  }, [open]);

  async function submit() {
    if (!email.trim()) {
      setErr(t("superAdmins.fieldEmail"));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await inviteSuperAdmin({ email: email.trim(), fullName: fullName.trim() || null });
      setResult({ inviteSent: r.inviteSent, setPasswordUrl: r.setPasswordUrl });
      onInvited();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      title={t("superAdmins.modalTitle")}
      onClose={onClose}
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            {t("forms.close")}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              {t("forms.cancel")}
            </Button>
            <Button variant="primary" onClick={submit} disabled={busy}>
              {busy ? t("superAdmins.inviteSending") : t("superAdmins.inviteAction")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3">
          <p className="text-sm">
            {result.inviteSent ? t("superAdmins.inviteSentInfo") : t("superAdmins.inviteNotEmailed")}
          </p>
          <Field label={t("superAdmins.setPasswordLink")}>
            <TextInput readOnly value={result.setPasswordUrl} onFocus={(e) => e.target.select()} />
          </Field>
        </div>
      ) : (
        <>
          <ErrorBanner message={err} />
          <p className="text-xs text-muted-foreground mb-3">{t("superAdmins.modalHint")}</p>
          <div className="space-y-3">
            <Field label={t("superAdmins.fieldEmail")} required>
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                placeholder="admin@platform.example"
              />
            </Field>
            <Field label={t("superAdmins.fieldName")}>
              <TextInput value={fullName} onChange={(e) => setFullName(e.target.value)} />
            </Field>
          </div>
        </>
      )}
    </Modal>
  );
}
